const GENERIC_IMPORT_FAILURE =
  "Something went wrong while importing the test data. Please check your file and try again.";

const REASON_LABELS = new Map<string, string>([["blank", "Sheet is empty"]]);

export type TdgExcelImportResultType = "success" | "partial" | "error";

export interface TdgImportSheetIssue {
  id: string;
  reason: string;
}

export interface TdgExcelImportResult {
  type: TdgExcelImportResultType;
  title: string;
  subTitle?: string;
  notFoundInFile?: string[];
  notFoundInTestSuite?: string[];
  invalidSheets?: TdgImportSheetIssue[];
}

export function tdgSheetReasonLabel(reason: string): string {
  const key = reason.trim();
  if (!key) {
    return "";
  }
  return REASON_LABELS.get(key) ?? key;
}

export function parseTdgExcelImportResult(
  source: unknown,
  options?: { forceError?: boolean }
): TdgExcelImportResult {
  const body = unwrapImportBody(source);
  const savedCount = readArrayLength(body, "saved_tables");
  const notFoundInFile = readStringArray(body, "not_found_in_file");
  const notFoundInTestSuite = readStringArray(body, "not_found_in_testsuite");
  const invalidSheets = readInvalidSheets(body);
  const hasFailures =
    invalidSheets.length > 0 ||
    notFoundInFile.length > 0 ||
    notFoundInTestSuite.length > 0;
  const topError = pickUserErrorMessage(body);

  let type: TdgExcelImportResultType;
  let title: string;
  let subTitle: string | undefined;

  if (!hasFailures) {
    if (options?.forceError) {
      type = "error";
      title = "Import Failed";
      subTitle = topError || GENERIC_IMPORT_FAILURE;
    } else {
      type = "success";
      title = "Import Successful";
      subTitle =
        topError ||
        (savedCount > 0
          ? `${savedCount} test case${savedCount === 1 ? "" : "s"} saved.`
          : undefined);
    }
  } else if (savedCount > 0) {
    type = "partial";
    title = "Import Partially Successful";
    subTitle = `${savedCount} test case${savedCount === 1 ? "" : "s"} saved. Some sheets could not be imported.`;
  } else {
    type = "error";
    title = "Import Failed";
    subTitle = topError || GENERIC_IMPORT_FAILURE;
  }

  return {
    type,
    title,
    subTitle,
    notFoundInFile: notFoundInFile.length ? notFoundInFile : undefined,
    notFoundInTestSuite: notFoundInTestSuite.length
      ? notFoundInTestSuite
      : undefined,
    invalidSheets: invalidSheets.length ? invalidSheets : undefined,
  };
}

function pickUserErrorMessage(body: Record<string, unknown> | null): string {
  const raw = readString(body, "error") || readString(body, "message");
  if (!raw || raw.startsWith("Http failure response")) {
    return "";
  }
  return raw;
}

function unwrapImportBody(source: unknown): Record<string, unknown> | null {
  const root = asRecord(source);
  if (!root) {
    return null;
  }

  const nestedBody = asRecord(root["body"]);
  if (nestedBody && isImportPayload(nestedBody)) {
    return nestedBody;
  }

  const nestedError = root["error"];
  const nestedErrorRecord = asRecord(nestedError);
  if (nestedErrorRecord && isImportPayload(nestedErrorRecord)) {
    return nestedErrorRecord;
  }

  if (isImportPayload(root)) {
    return root;
  }

  if (nestedErrorRecord) {
    return nestedErrorRecord;
  }

  return root;
}

function isImportPayload(record: Record<string, unknown>): boolean {
  return (
    "invalid_sheets" in record ||
    "saved_tables" in record ||
    "not_found_in_file" in record ||
    "not_found_in_testsuite" in record
  );
}

function readInvalidSheets(
  body: Record<string, unknown> | null
): TdgImportSheetIssue[] {
  if (!body || !("invalid_sheets" in body) || !Array.isArray(body["invalid_sheets"])) {
    return [];
  }
  const rows: TdgImportSheetIssue[] = [];
  for (const entry of body["invalid_sheets"]) {
    const record = asRecord(entry);
    if (!record) {
      continue;
    }
    const id =
      readString(record, "testCaseId").trim() ||
      readString(record, "sheet").trim();
    if (!id) {
      continue;
    }
    const reasonKey = readString(record, "reason");
    const reason =
      tdgSheetReasonLabel(reasonKey) ||
      readString(record, "message").trim() ||
      reasonKey;
    rows.push({ id, reason });
  }
  return rows;
}

function readStringArray(
  record: Record<string, unknown> | null,
  key: string
): string[] {
  if (!record || !(key in record) || !Array.isArray(record[key])) {
    return [];
  }
  return (record[key] as unknown[])
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function readArrayLength(
  record: Record<string, unknown> | null,
  key: string
): number {
  if (!record || !(key in record) || !Array.isArray(record[key])) {
    return 0;
  }
  return (record[key] as unknown[]).length;
}

function readString(
  record: Record<string, unknown> | null,
  key: string
): string {
  if (!record || !(key in record)) {
    return "";
  }
  const value = record[key];
  return typeof value === "string" ? value : "";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}
